import { useState } from 'react';
import SafeIcon from './SafeIcon';
import { useApp } from '../i18n/AppContext';
import type { BrandColour, ColourRole } from '../store/seed';

const colourRoles: ColourRole[] = [
  'Primary',
  'Secondary',
  'Accent',
  'Background',
  'Text',
  'Light accent',
  'Custom',
];
const SHORT_HEX_PATTERN = /^#[0-9A-F]{3}$/;
const FULL_HEX_PATTERN = /^#[0-9A-F]{6}$/;
const RGB_CHANNEL_OFFSETS_IN_HEX = [1, 3, 5];

const normaliseHex = (value: string) => {
  const hex = value.trim().replace(/^#?/, '#').toUpperCase();
  if (SHORT_HEX_PATTERN.test(hex))
    return `#${hex
      .slice(1)
      .split('')
      .map((hexDigit) => hexDigit + hexDigit)
      .join('')}`;
  return hex;
};
const isFullHex = (hex: string) => FULL_HEX_PATTERN.test(hex);
const hexToRgbChannels = (hex: string) =>
  RGB_CHANNEL_OFFSETS_IN_HEX.map((offset) => parseInt(hex.slice(offset, offset + 2), 16));

export default function ColourEditor({
  colour,
  existing,
  onCancel,
  onSave,
  onRemove,
}: {
  colour?: BrandColour & { note?: string };
  existing: BrandColour[];
  onCancel: () => void;
  onSave: (colour: BrandColour & { note?: string }) => void;
  onRemove?: () => void;
}) {
  const { translate } = useApp();
  const [name, setName] = useState(colour?.name ?? '');
  const [hexInput, setHexInput] = useState(colour?.hex ?? '#');
  const [role, setRole] = useState<ColourRole>(colour?.role ?? 'Accent');
  const [note, setNote] = useState(colour?.note ?? '');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const hex = normaliseHex(hexInput);
  const isValid = isFullHex(hex);
  const duplicate = isValid
    ? existing.find((item) => item.hex.toUpperCase() === hex && item.name !== colour?.name)
    : undefined;
  const nameTaken = existing.some(
    (item) =>
      item.name.trim().toLowerCase() === name.trim().toLowerCase() && item.name !== colour?.name
  );
  const canSave = isValid && name.trim() && !nameTaken;

  function setRgbChannel(channelIndex: number, value: string) {
    if (!isValid) return;
    const channels = hexToRgbChannels(hex);
    channels[channelIndex] = Math.max(0, Math.min(255, Number(value) || 0));
    setHexInput(
      `#${channels
        .map((channelValue) => channelValue.toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase()}`
    );
  }

  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <section
        className="drawer colour-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="colour-editor-title"
      >
        <div className="drawer-header">
          <div>
            <h2 id="colour-editor-title">{translate(colour ? 'Edit colour' : 'Add a colour')}</h2>
            <div className="muted">
              {translate('Saved to the brand draft. Approved posts keep their colours.')}
            </div>
          </div>
          <button className="icon-button" onClick={onCancel} aria-label={translate('Close')}>
            <SafeIcon name="FiX" />
          </button>
        </div>
        <div className="colour-editor-swatch" style={{ background: isValid ? hex : 'transparent' }}>
          {!isValid && <span className="muted">{translate('Enter a HEX code to preview')}</span>}
        </div>
        <div className="colour-editor-grid">
          <label>
            <span>{translate('Pick a colour')}</span>
            <input
              type="color"
              value={isValid ? hex.toLowerCase() : '#000000'}
              onChange={(event) => setHexInput(event.target.value.toUpperCase())}
              aria-label={translate('Pick a colour')}
            />
          </label>
          <label>
            <span>HEX</span>
            <input
              className="text-field"
              value={hexInput}
              onChange={(event) => setHexInput(event.target.value)}
              aria-invalid={!isValid}
              placeholder="#365A48"
            />
          </label>
          {isValid && (
            <div className="colour-rgb" aria-label="RGB">
              {['R', 'G', 'B'].map((channel, channelIndex) => (
                <label key={channel}>
                  <span>{channel}</span>
                  <input
                    className="text-field"
                    type="number"
                    min={0}
                    max={255}
                    value={hexToRgbChannels(hex)[channelIndex]}
                    onChange={(event) => setRgbChannel(channelIndex, event.target.value)}
                  />
                </label>
              ))}
            </div>
          )}
        </div>
        {!isValid && hexInput.length > 1 && (
          <p className="field-error" role="alert">
            {translate('Use six characters after #, for example #365A48.')}
          </p>
        )}
        {duplicate && (
          <p className="notice-banner">
            <SafeIcon name="FiInfo" />
            {translate(
              '{name} already uses this colour. You can still save it with another role.',
              {
                name: duplicate.name,
              }
            )}
          </p>
        )}
        <label htmlFor="colour-name">{translate('Colour name')}</label>
        <input
          id="colour-name"
          className="text-field"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={translate('For example: Forest green')}
          aria-invalid={nameTaken}
        />
        {nameTaken && (
          <p className="field-error">{translate('Another colour already has this name.')}</p>
        )}
        <label htmlFor="colour-role" style={{ display: 'block', marginTop: 12 }}>
          {translate('Role')}
        </label>
        <select
          id="colour-role"
          className="select-field"
          value={role}
          onChange={(event) => setRole(event.target.value as ColourRole)}
        >
          {colourRoles.map((item) => (
            <option key={item} value={item}>
              {translate(item)}
            </option>
          ))}
        </select>
        <label htmlFor="colour-note" style={{ display: 'block', marginTop: 12 }}>
          {translate('When to use it (optional)')}
        </label>
        <input
          id="colour-note"
          className="text-field"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder={translate('For example: offer badges only')}
        />
        <div className="button-row" style={{ marginTop: 20 }}>
          <button
            className="button primary"
            disabled={!canSave}
            onClick={() => onSave({ name: name.trim(), hex, role, note: note.trim() || undefined })}
          >
            <SafeIcon name="FiCheck" />
            {translate('Save to draft')}
          </button>
          <button className="button" onClick={onCancel}>
            {translate('Cancel')}
          </button>
          {onRemove && !confirmRemove && (
            <button className="button danger-outline" onClick={() => setConfirmRemove(true)}>
              <SafeIcon name="FiTrash2" />
              {translate('Remove')}
            </button>
          )}
        </div>
        {confirmRemove && onRemove && (
          <div className="notice-banner status-panel-inline warn" style={{ marginTop: 12 }}>
            <SafeIcon name="FiAlertTriangle" />
            <span>
              {translate(
                'Remove {name} from the brand draft? Readability checks that use it are removed too.',
                { name: colour?.name ?? '' }
              )}
            </span>
            <button className="button small danger" onClick={onRemove}>
              {translate('Remove colour')}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
